import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { ContentTestDto } from '@inference.engine/persona-api-client';
import { contentTestResolutionQuery, CONTENT_TEST_QUERY_KEYS } from '@/entities/content-test';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { DeepLinkInterstitial, Loading } from '@/shared/ui';
import { WizardLayout } from '@/shared/ui/layout';
import type { StepState } from '@/shared/ui/StepTracker/StepTracker';
import { ROUTES } from '@/shared/config/routes';
import { useDeepLinkResolution, isTerminalResolution } from '@/shared/hooks';
import { CancelTestDialog } from './components/CancelTestDialog';
import { RunProgressLeftRail } from './components/RunProgressLeftRail';
import { RunResultLeftRail } from './components/RunResultLeftRail';
import { RunSpinnerPane } from './components/RunSpinnerPane';
import { contentTestSteps, contentTestWizardTitle } from './constants';
import { useContentTestPolling } from './model/useContentTestPolling';
import { useRunContentTest } from './model/useRunContentTest';
import { useTranslation } from 'react-i18next';

// ResultsReport pulls Recharts into its module graph. Lazy-load so the bundle
// only ships when we actually reach the completed state.
const ResultsReport = lazy(() =>
  import('./components/ResultsReport').then((m) => ({ default: m.ResultsReport })),
);

type Status = ContentTestDto['status'];

export const Detail = () => {
  const { t } = useTranslation('contentTest');

  const { contentTestId } = useParams<{ contentTestId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  // Deep-link seam: resolve the id's owning workspace first and
  // hold the content-test fetch until resolution settles — under the wrong
  // workspace header it would 404 into the null/not-found branch prematurely.
  const resolution = useDeepLinkResolution({
    resolutionQuery: contentTestResolutionQuery(contentTestId),
    enabled: !!contentTestId,
  });

  const { data: test, isPending } = useContentTestPolling(contentTestId, {
    enabled: resolution.status === 'ready',
    // The seam's interstitial owns the not-found UX, not the global toast.
    suppressErrorNotification: true,
  });
  const runMutation = useRunContentTest(contentTestId ?? '');
  const fired = useRef(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // Mirrors the Focus Group report: the results view can collapse its left
  // configuration panel via the PanelLeft toggle in the report header.
  const [showPanel, setShowPanel] = useState(true);

  useEffect(() => {
    if (!test || !contentTestId || fired.current) return;
    if (test.status !== 'draft') return;
    fired.current = true;
    // The auto-fire must run exactly once per mount. Resetting `fired` from
    // onError would re-enter the effect on the next render (runMutation
    // identity changes with state) and cause an infinite retry loop. Retry
    // is exclusively user-driven via the Retry button.
    runMutation.mutate();
  }, [test, contentTestId, runMutation]);

  // Polling stops at a terminal status, so nothing else would refresh the
  // Overview row: it would keep serving the cached "running" for the rest of
  // its staleTime. Invalidate once, on the transition into terminal.
  const settledStatus = useRef<Status | null>(null);
  useEffect(() => {
    const status = test?.status;
    if (status !== 'completed' && status !== 'failed') return;
    if (settledStatus.current === status) return;

    settledStatus.current = status;
    if (workspaceId) {
      void queryClient.invalidateQueries({
        queryKey: CONTENT_TEST_QUERY_KEYS.list(workspaceId),
      });
    }
  }, [test?.status, workspaceId, queryClient]);

  const handleRetry = () => {
    if (runMutation.isPending) return;
    // fired.current is already true from the auto-fire that landed us on the
    // error state; keep it true so the auto-fire effect doesn't re-enter when
    // runMutation's identity changes mid-retry.
    runMutation.mutate();
  };

  const handleCancelConfirm = () => {
    setCancelOpen(false);
    void navigate(ROUTES.TOOLS_CONTENT_TEST, { replace: true });
  };

  // Terminal deep-link states own the page and must win over the loading /
  // null-result handling below. Only reachable while the flag is on — flag off
  // pins resolution to 'ready', so this branch is dead code in that mode.
  if (isTerminalResolution(resolution)) {
    return <DeepLinkInterstitial state={resolution} entity="contentTest" />;
  }

  // Resolver in flight, the fetch itself pending, or a null result (id not in
  // the active workspace). With the flag off this collapses to today's exact
  // `isPending || !test` guard.
  if (resolution.status === 'resolving' || isPending || !test) {
    return (
      <div data-testid="detail-loading">
        <Loading />
      </div>
    );
  }

  const goToNewTest = () => void navigate(ROUTES.TOOLS_CONTENT_TEST_NEW);
  const inProgressView: { leftRail: ReactNode; rightPane: ReactNode } = {
    leftRail: (
      <RunProgressLeftRail
        runIsError={runMutation.isError}
        onRetry={handleRetry}
        onCancelClick={() => setCancelOpen(true)}
      />
    ),
    rightPane: <RunSpinnerPane />,
  };
  const viewByStatus: Record<Status, { leftRail: ReactNode; rightPane: ReactNode }> = {
    draft: inProgressView,
    running: inProgressView,
    completed: {
      leftRail: <RunResultLeftRail variant="ready" onNewTest={goToNewTest} />,
      rightPane: (
        <Suspense fallback={<Loading />}>
          <ResultsReport test={test} onTogglePanel={() => setShowPanel((v) => !v)} />
        </Suspense>
      ),
    },
    failed: {
      leftRail: (
        <RunResultLeftRail
          variant="failed"
          onRetry={handleRetry}
          retryPending={runMutation.isPending}
          onNewTest={goToNewTest}
        />
      ),
      rightPane: (
        <div className="p-8">
          <p className="text-base font-medium text-foreground">
            {t('contentTest.detail.weCouldnTCompleteThis')}
          </p>
          <p className="mt-2 block text-sm text-muted-foreground">
            {t('contentTest.detail.retryTheRunOrStart')}
          </p>
        </div>
      ),
    },
  };
  const view = viewByStatus[test.status];
  // Setup is complete once we have a saved test. Run is active whenever the user is
  // on the Detail page (in-flight runs and the results view both keep "Run" highlighted),
  // and inactive only when the test has failed.
  const runState: StepState = test.status === 'failed' ? 'inactive' : 'active';
  // Setup stays complete on every Detail view; only the Run step reflects runState.
  // No single activeStep can express "Setup complete, Run inactive" (the failed
  // case), so drive the tracker with explicit step states.
  const stepStates: ReadonlyArray<StepState> = ['complete', runState];

  return (
    <>
      <WizardLayout
        configTitle={contentTestWizardTitle()}
        steps={contentTestSteps()}
        activeStep={runState === 'active' ? 1 : -1}
        stepStates={stepStates}
        onBack={() => void navigate(ROUTES.TOOLS_CONTENT_TEST)}
        configContent={view.leftRail}
        showConfigPanel={test.status === 'completed' ? showPanel : true}
      >
        {view.rightPane}
      </WizardLayout>
      <CancelTestDialog
        open={cancelOpen}
        onConfirm={handleCancelConfirm}
        onClose={() => setCancelOpen(false)}
      />
    </>
  );
};

Detail.displayName = 'ContentTestDetail';
